/* ============================================================
   Site-level constants — one source of truth.

   Ported from the pattern used on the sister JK & Associates site,
   where brand, contact, version and legal metadata live in one object
   and every page reads from it. The alternative — hand-editing the same
   string across twenty pages — is how footers drift, and on that site
   the primary navigation had already drifted before it was centralised.
   ============================================================ */

export const SITE = {
  name: 'Kanda',
  legalName: 'Kanda Logistics Advisory Ltd',
  /* The practice name, without the suffix the certificate has not
     issued yet. The footer carries this while incorporation is pending
     and `legalName` once it completes — see `publishedName` below.

     The distinction is worth the extra field. "Ltd" is a statement that
     a company exists on a register, and it appeared unqualified on
     every one of the site's routes while the legal pages two clicks
     away said incorporation was still in progress. Of the two, the
     footer is the one nearly every visitor reads. */
  tradingName: 'Kanda Logistics Advisory',
  descriptor: 'Logistics Advisory',
  tagline: 'The belt that moves Africa',
  blurb:
    'Corridor diagnostics, regulatory navigation and cost benchmarking for African freight — ' +
    'computed entirely on your device.',
  email: 'hello@kanda.africa',
  location: '2nd Avenue, Cargo Terminal, JKIA, Nairobi',
  version: 'v0.3 — Move track complete',
  // The canonical home. Every canonical link, OG tag, JSON-LD url and
  // the sitemap derive from this, so the domain moves in one place.
  // Changing it after a site has been indexed means asking a search
  // engine to re-crawl everything it already knows — which is why this
  // was worth doing before the first production deploy rather than after.
  origin: 'https://kandalogistics.enterprises',
  jurisdiction: 'Kenya',
  dataProtectionAct: 'Data Protection Act, 2019 (Kenya)',
  regulator: 'Office of the Data Protection Commissioner (ODPC)',
  lastUpdated: '2 August 2026',

  /* The company is mid-incorporation, so there is no registration number
     yet. Stated as a fact rather than left as a bracketed blank — a
     notice that says "incorporation in progress" is accurate; one
     carrying an invented company number is not.

     ON THE DAY THE CERTIFICATE ISSUES, exactly two edits:
       1. incorporationPending → false
       2. registrationNumber → the number on the certificate
     Everything below derives from those. An earlier version of this
     block claimed the flag alone was enough while the pending sentence
     sat hardcoded beside it, so flipping the flag would have left three
     pages still saying incorporation was in progress. The claim is now
     true because the sentence is built rather than stored. */
  incorporationPending: true,
  registrationNumber: '',

  /* How the entity identifies itself in the legal pages: the intended
     name, with its current standing attached. Both halves matter — the
     name alone would overstate, the standing alone would be uselessly
     vague. */
  get registeredEntity() {
    if (this.incorporationPending) {
      return `${this.legalName} — incorporation in progress; registration number to follow`;
    }
    return this.registrationNumber
      ? `${this.legalName} · Registration no. ${this.registrationNumber}`
      : this.legalName;
  },

  registeredAddress:
    '2nd Avenue, Cargo Terminal, Jomo Kenyatta International Airport, Nairobi, Kenya',

  /* The name published as a bare assertion, with nothing qualifying it
     alongside: the footer copyright on every route, and — the reason
     this needs care — Organization.name and every tool's provider.name
     in the JSON-LD, which is what a search engine ingests and
     republishes to people who will never read the notice.

     Not `legalName` while incorporation is pending, because "Ltd" in
     those positions is an unaccompanied claim that a company exists on
     a register. The legal pages use `registeredEntity` instead, which
     carries its own qualification. */
  get publishedName() {
    return this.incorporationPending ? this.tradingName : this.legalName;
  },

  /* Secondary strapline from the letterhead. */
  strapline: 'Smarter routes. Stronger connections. Sustainable futures.',

  sister: {
    name: 'JK & Associates',
    descriptor: 'Aviation Advisory · Africa',
    url: 'https://jkassociates.enterprises',
    blurb: 'End-to-end aviation advisory for African operators and investors.'
  }
};

/* ============================================================
   About — the footer's operator disclosure.

   Prose lives here rather than in the component for the same reason
   every other string does: one place to change it. The numbers do NOT
   live here. They are counted off the registries at render time, so a
   claim like "fifteen tools" cannot outlive the fifteenth tool — the
   drift that a hand-written About section attracts faster than any
   other block on a site, because nobody re-reads it.

   `limits` is not an afterthought. A product whose pitch is "verified,
   cited, current" earns that by publishing what it cannot do, in the
   same breath and the same type size as what it can. It is the part a
   reader checks the rest of the page against.
   ============================================================ */
export const ABOUT = {
  path: '/about-us',
  heading: 'About us',
  /* The footer gets these two sentences and a link — nothing more.

     An earlier draft put four paragraphs here and it was mostly other
     pages' work restated. "Everything runs in your browser, no accounts,
     no analytics" already appears on the home page, on Method & sources
     and in the privacy notice; the coverage limits are Method's whole
     job. A fourth copy adds no information and guarantees that one day
     three of them will disagree.

     So this says the one thing no other page says — who the practice is
     — and sends the reader to whichever page owns the rest. */
  lead:
    'Kanda is a diagnostic practice for African freight corridors, working out of the ' +
    'cargo terminal at Jomo Kenyatta International Airport in Nairobi.',
  leadMore:
    'It turns the questions an operator already argues about internally into figures with ' +
    'a stated basis and a date attached.',
  linkLabel: 'Who we are, and how to work with us'
};

/** Footer reference strip — the material people need when they get stuck. */
export const LEARN_LINKS = [
  { path: '/about', label: 'Method & sources', hint: 'How every figure is arrived at, and its limits' },
  { path: '/faq', label: 'FAQ', hint: 'Straight answers, including the awkward ones' },
  { path: '/glossary', label: 'Glossary', hint: 'The vocabulary, and what goes wrong around it' },
  { path: '/compliance', label: 'Compliance Calendar', hint: 'Every instrument, with live status' }
];

export const LEGAL_LINKS = [
  { path: '/privacy', label: 'Privacy Notice' },
  { path: '/terms', label: 'Terms of Use' }
];
